import { Component, HostListener } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MatListModule } from '@angular/material/list';
import { TruckSearchComponent } from '@app/trucks/truck-search/truck-search.component';
import { TruckSearchFiltersDialogComponent } from '@app/trucks/truck-search-filters-dialog/truck-search-filters-dialog.component';
import { TruckSearch } from '@app/trucks/models/truck-search.model';
import { AppService } from '@app/app.service';
import { MatDialog } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { AuthService } from '@app/auth/auth.service';
import { Router } from '@angular/router';
import { LoadingOverlayService } from '@app/shared/loading-overlay/loading-overlay.service';
import { UserPlan } from '@app/auth/user.model';
import { UserSettings } from '@app/auth/user.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-truck-searches',
  imports: [
    TruckSearchComponent,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    RouterModule,
    MatListModule,
    MatTooltipModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './truck-searches.component.html',
  styleUrl: './truck-searches.component.scss',
})
export class TruckSearchesComponent {
  truckSearches: TruckSearch[] = [];
  selectedIndex = 0;

  loadToMatch: any = null;

  loading = false;

  userPlan: UserPlan | null = null;
  userSettings: UserSettings | null = null;
  defaultSearchRadius: number | null = null;

  isMobile = window.innerWidth < 768;
  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  constructor(
    private dialog: MatDialog,
    private appService: AppService,
    private authService: AuthService,
    private router: Router,
    private loadingOverlayService: LoadingOverlayService
  ) { }

  ngOnInit(): void {

    if (!this.authService.userCanSearchTrucks()) {
      this.appService.navigateToDefaultUrl();
      return;
    }

    this.userPlan = this.authService.getUserPlanFromStorage();
    this.userSettings = this.authService.getUserSettingsFromStorage();
    this.defaultSearchRadius = this.userSettings?.default_search_radius_miles ?? 250;

    this.getTruckSearches();

    this.loadToMatch = history.state.loadToMatch;

    if (this.loadToMatch) {
      this.matchTruck(this.loadToMatch);
    }

  }

  getTruckSearches(selectedSearchId?: number): void {

    this.loading = true;
    this.loadingOverlayService.show();

    this.appService.get('trucks/searches').subscribe({
      next: (res) => {

        this.truckSearches = res.data ?? [];

        if (selectedSearchId) {
          const index = this.truckSearches.findIndex(
            (search) => search.id === selectedSearchId
          );

          if (index !== -1) {
            this.selectedIndex = index;
          }
        } else {
          const defaultIndex = this.truckSearches.findIndex(
            (search) => search.is_default
          );

          this.selectedIndex = defaultIndex !== -1 ? defaultIndex : 0;
        }

        if (res.time < 0.5) {
          // Ensure spinner shows for at least 500ms to prevent flickering
          setTimeout(() => {
            this.loadingOverlayService.hide();
          }, 500);
        } else {
          this.loadingOverlayService.hide();
        }
        this.loading = false;
      },
      error: (err) => {
        this.truckSearches = [];
        this.appService.showSnackBar(err.error.message, 'error');
        this.loadingOverlayService.hide();
        this.loading = false;
      },
    });
  }

  // Create a new truck search
  openFiltersDialog(filters: any = {}): void {

    const maxSavedTruckSearches = this.userPlan?.max_saved_truck_searches ?? 0;
    
    if (this.truckSearches.length >= maxSavedTruckSearches) {
      this.appService.warningSnackBar(`Your plan allows a maximum of ${maxSavedTruckSearches} saved truck searches.`);
      return;
    }

    const dialogRef = this.dialog.open(TruckSearchFiltersDialogComponent, {
      width: '1000px',
      data: { filters: filters },
    });

    dialogRef.afterClosed().subscribe((result: TruckSearch | undefined) => {
      if (!result) {
        return;
      }

      this.truckSearches.push(result);
      this.selectedIndex = this.truckSearches.length - 1;

      if(this.isMobile) {
        this.router.navigate(['/trucks/searches/' + result.id]);
      }
    });
  }

  deleteSearch(search: TruckSearch, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    this.appService.delete(`trucks/searches/${search.id}`).subscribe({
      next: (res) => {

        const deletedIndex = this.truckSearches.findIndex(
          (item) => item.id === search.id
        );

        this.truckSearches = this.truckSearches.filter(
          (item) => item.id !== search.id
        );

        if (this.truckSearches.length === 0) {
          this.selectedIndex = 0;
          return;
        }

        if (this.selectedIndex >= this.truckSearches.length) {
          this.selectedIndex = this.truckSearches.length - 1;
        } else if (deletedIndex < this.selectedIndex) {
          this.selectedIndex = this.selectedIndex - 1;
        }
      },
      error: (err) => {
        this.appService.showSnackBar(err.error.message, 'error');
      },
    });
  }

  matchTruck(load: any): void {
    if (!load) {
      return;
    }

    const filters = {
      origin: load.origin,
      origin_latitude: load.origin_latitude,
      origin_longitude: load.origin_longitude,
      origin_radius: this.defaultSearchRadius,
    };

    this.openFiltersDialog(filters);

    history.replaceState({}, '', this.router.url);
  }

}